import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { getSalatOrder } from "../../api";
import ConvertToArabicNumbers from "../../utils/convert_numbers";

function SalatOrderConfirmation() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { orderId } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(!location.state?.order);

  useEffect(() => {
    if (order) return;
    getSalatOrder(orderId)
      .then(setOrder)
      .finally(() => setLoading(false));
  }, [orderId, order]);

  const formatMoney = (value) => {
    const formatted = value.toFixed(2);
    return isRtl ? ConvertToArabicNumbers(formatted) : formatted;
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen text-xl">{t("common.loading")}</div>;
  }

  if (!order) {
    return <div className="flex justify-center items-center h-screen text-xl">{t("salatMahsool.orders.notFound")}</div>;
  }

  return (
    <div dir={isRtl ? "rtl" : "ltr"} className="w-full lg:px-20 px-4 py-8">
      <Helmet>
        <title>{t("salatMahsool.confirmation.title")} - Mahsool</title>
      </Helmet>

      <div className="max-w-2xl mx-auto bg-white shadow-xl rounded-lg p-8 text-center">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-primary mb-2">{t("salatMahsool.confirmation.title")}</h1>
        <p className="text-gray-600 mb-6">{t("salatMahsool.confirmation.thankYou")}</p>

        <div className="text-start bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex justify-between mb-2">
            <span className="text-gray-500">{t("salatMahsool.confirmation.orderNumber")}</span>
            <span className="font-bold">#{order.id}</span>
          </div>
          <div className="flex justify-between mb-2">
            <span className="text-gray-500">{t("salatMahsool.confirmation.status")}</span>
            <span className="font-bold">{t(`salatMahsool.status.${order.status}`)}</span>
          </div>

          <div className="border-t my-3"></div>

          {order.items?.map((line) => (
            <div key={line.id} className="flex justify-between text-sm mb-1">
              <span>
                {line.product_name} × {line.quantity}
              </span>
              <span>
                {formatMoney(parseFloat(line.subtotal))} {t("common.currency")}
              </span>
            </div>
          ))}

          <div className="border-t my-3"></div>

          <div className="flex justify-between font-bold text-primary">
            <span>{t("salatMahsool.confirmation.total")}</span>
            <span>
              {formatMoney(parseFloat(order.total_amount))} {t("common.currency")}
            </span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/home/profile/salat-orders"
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
          >
            {t("salatMahsool.confirmation.viewOrders")}
          </Link>
          <Link
            to="/home/salatMahsool"
            className="px-6 py-2 border border-primary text-primary rounded-lg hover:bg-primary/10"
          >
            {t("salatMahsool.confirmation.backToCatalog")}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default SalatOrderConfirmation;
